import { Controller } from "@hotwired/stimulus"

// Loops a typewriter effect on the voice demo transcript:
// type → hold → erase → hold → type again. Pauses while off-screen.
export default class extends Controller {
  static targets = ["text"]
  static values = {
    text: String,
    typeSpeed: { type: Number, default: 70 },
    eraseSpeed: { type: Number, default: 35 },
    holdFull: { type: Number, default: 1400 },
    holdEmpty: { type: Number, default: 600 }
  }

  connect() {
    this.timers = []
    this.visible = true

    if (typeof IntersectionObserver !== "undefined") {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting
          if (this.visible) this._start()
          else this._cancel()
        })
      }, { threshold: 0.2 })
      this.observer.observe(this.element)
    }

    this._start()
  }

  disconnect() {
    this._cancel()
    this.observer?.disconnect()
  }

  _start() {
    this._cancel()
    this._typeIn()
  }

  _cancel() {
    this.timers.forEach((id) => clearTimeout(id))
    this.timers = []
  }

  _setText(value) {
    if (this.hasTextTarget) this.textTarget.textContent = value
  }

  _typeIn() {
    const full = this.textValue || ""
    let i = 0
    const tick = () => {
      if (!this.visible) return
      i++
      this._setText(full.slice(0, i))
      if (i < full.length) {
        this.timers.push(setTimeout(tick, this.typeSpeedValue))
      } else {
        this.timers.push(setTimeout(() => this._eraseOut(), this.holdFullValue))
      }
    }
    this._setText("")
    this.timers.push(setTimeout(tick, this.typeSpeedValue))
  }

  _eraseOut() {
    const full = this.textValue || ""
    let i = full.length
    const tick = () => {
      if (!this.visible) return
      i--
      this._setText(full.slice(0, Math.max(0, i)))
      if (i > 0) {
        this.timers.push(setTimeout(tick, this.eraseSpeedValue))
      } else {
        this.timers.push(setTimeout(() => this._typeIn(), this.holdEmptyValue))
      }
    }
    this.timers.push(setTimeout(tick, this.eraseSpeedValue))
  }
}
